import { format, parseISO, isValid, isSameDay, isSameMonth, isSameYear, addDays, subDays, addMonths, subMonths, addYears, subYears, parse } from 'date-fns';

/**
 * Helper function to convert various date formats to Date object
 * Similar to moment(date)
 *
 * Handles the following formats:
 * - Date objects
 * - ISO 8601 strings (e.g., "1987-10-11T07:00:00Z", "1987-10-11T07:00:00.000Z")
 * - MM/dd/yyyy format strings
 * - Standard Date.parse() compatible strings
 */
const MIN_DISPLAYABLE_YEAR = 1900;

export const toDate = (date: Date | string | null | undefined): Date | null => {
    if (!date) return null;
    if (date instanceof Date) return isValid(date) ? date : null;
    if (typeof date === 'string') {
        // Try parseISO first for ISO 8601 strings (includes formats with 'T' or 'Z')
        // This handles formats like "1987-10-11T07:00:00Z"
        if (date.includes('T') || date.includes('Z')) {
            const parsed = parseISO(date);
            if (isValid(parsed)) return parsed;
        }
        // Try parsing MM/dd/yyyy format
        const parsed = parse(date, 'MM/dd/yyyy', new Date());
        if (isValid(parsed)) return parsed;
        // Fallback to standard Date parsing
        const dateObj = new Date(date);
        return isValid(dateObj) ? dateObj : null;
    }
    return null;
};

/**
 * True when the value parses to a real calendar date (rejects DateTime.MinValue / year 0001).
 */
export const isDisplayableDate = (date: Date | string | null | undefined): boolean => {
    const dateObj = toDate(date);
    return dateObj !== null && dateObj.getFullYear() >= MIN_DISPLAYABLE_YEAR;
};

/**
 * Format date using date-fns format patterns
 * Similar to moment().format()
 */
export const formatDate = (date: Date | string | null | undefined, formatStr: string): string => {
    const dateObj = toDate(date);
    if (!dateObj) return '';
    return format(dateObj, formatStr);
};

/**
 * Check if date is valid
 * Similar to moment().isValid()
 */
export const isValidDate = (date: Date | string | null | undefined): boolean => {
    const dateObj = toDate(date);
    return dateObj !== null;
};

/**
 * Check if two dates are the same day
 * Similar to moment().isSame(date, 'day')
 */
export const isSameDayDate = (date1: Date | string | null | undefined, date2: Date | string | null | undefined): boolean => {
    const d1 = toDate(date1);
    const d2 = toDate(date2);
    if (!d1 || !d2) return false;
    return isSameDay(d1, d2);
};

/**
 * Check if two dates are the same month
 * Similar to moment().isSame(date, 'month')
 */
export const isSameMonthDate = (date1: Date | string | null | undefined, date2: Date | string | null | undefined): boolean => {
    const d1 = toDate(date1);
    const d2 = toDate(date2);
    if (!d1 || !d2) return false;
    return isSameMonth(d1, d2);
};

/**
 * Check if two dates are the same year
 * Similar to moment().isSame(date, 'year')
 */
export const isSameYearDate = (date1: Date | string | null | undefined, date2: Date | string | null | undefined): boolean => {
    const d1 = toDate(date1);
    const d2 = toDate(date2);
    if (!d1 || !d2) return false;
    return isSameYear(d1, d2);
};

/**
 * Add days to a date
 * Similar to moment().add(days, 'days')
 */
export const addDaysToDate = (date: Date | string | null | undefined, days: number): Date | null => {
    const dateObj = toDate(date);
    if (!dateObj) return null;
    return addDays(dateObj, days);
};

/**
 * Subtract days from a date
 * Similar to moment().subtract(days, 'days')
 */
export const subtractDaysFromDate = (date: Date | string | null | undefined, days: number): Date | null => {
    const dateObj = toDate(date);
    if (!dateObj) return null;
    return subDays(dateObj, days);
};

/**
 * Add months to a date
 * Similar to moment().add(months, 'months')
 */
export const addMonthsToDate = (date: Date | string | null | undefined, months: number): Date | null => {
    const dateObj = toDate(date);
    if (!dateObj) return null;
    return addMonths(dateObj, months);
};

/**
 * Subtract months from a date
 * Similar to moment().subtract(months, 'months')
 */
export const subtractMonthsFromDate = (date: Date | string | null | undefined, months: number): Date | null => {
    const dateObj = toDate(date);
    if (!dateObj) return null;
    return subMonths(dateObj, months);
};

/**
 * Add years to a date
 * Similar to moment().add(years, 'years')
 */
export const addYearsToDate = (date: Date | string | null | undefined, years: number): Date | null => {
    const dateObj = toDate(date);
    if (!dateObj) return null;
    return addYears(dateObj, years);
};

/**
 * Subtract years from a date
 * Similar to moment().subtract(years, 'years')
 */
export const subtractYearsFromDate = (date: Date | string | null | undefined, years: number): Date | null => {
    const dateObj = toDate(date);
    if (!dateObj) return null;
    return subYears(dateObj, years);
};
